// src/pages/Role.js
import React, { useEffect, useState, useContext } from 'react';
import {
  Button,
  Dialog,
  IconButton,
  Paper,
  Stack,
  Typography,
  Box,
  Snackbar,
  Alert,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  FormControlLabel,
  Switch,
  Menu,
  MenuItem,
  Breadcrumbs,
  TablePagination,
  InputAdornment,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSelector } from 'react-redux';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import BadgeIcon from '@mui/icons-material/Badge';
import AddIcon from '@mui/icons-material/Add';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import { Link } from 'react-router-dom';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { request } from '../utils/request';
import { toast } from '../utils/toast';
import { ColorModeContext } from '../index';
import CommonDeleteModal from '../components/Common/CommonDeleteModal';

export const Role = () => {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === 'dark';
  const organizationData = useSelector((state) => state.organization);
  const modules = organizationData.find(item => item.id === 'hospital')?.options || [];
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.superadmin === true;
  const userPermissions = currentUser.permissions || [];

  const checkPerm = (action) => {
    if (isSuperAdmin) return true;

    return userPermissions.some((permission) => {
      const normalizedPermission = String(permission).toLowerCase();
      return (
        (normalizedPermission.includes('role') ||
          normalizedPermission.includes('roles') ||
          normalizedPermission.includes('role master')) &&
        normalizedPermission.includes(action.toLowerCase())
      );
    });
  };

  const canCreate = checkPerm('write');
  const canEdit = checkPerm('update');
  const canDelete = checkPerm('delete');

  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [roles, setRoles] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingRole, setDeletingRole] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);

  useEffect(() => {
    setPage(0);
  }, [roles.length]);

  const paginatedRoles = roles.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const handleChangePage = (event, nextPage) => setPage(nextPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(id);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };
  const onMenuView = (record) => {
    handleMenuClose();
    openDialog('view', record);
  };
  const onMenuEdit = (record) => {
    if (!canEdit) return;
    handleMenuClose();
    openDialog('edit', record);
  };
  const onMenuDelete = (record) => {
    if (!canDelete) return;
    handleMenuClose();
    handleDeleteClick(record);
  };

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');

  const validationSchema = Yup.object().shape({
    role: Yup.string().required('Role is required.'),
  });

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: { role: '', description: '', status: 'Active' },
  });
  const { handleSubmit, reset, setValue, control, setError, clearErrors } = methods;

  const [permissions, setPermissions] = useState({});
  const [users, setUsers] = useState([]);
  const [expandedModules, setExpandedModules] = useState({});

  const allModulesExpanded = modules.every(m => {
    const children = m.menu || (m.to ? [m] : []);
    return children.length === 0 || expandedModules[m.id];
  });

  const handleToggleExpandAll = () => {
    if (allModulesExpanded) {
      setExpandedModules({});
    } else {
      const newExpanded = {};
      modules.forEach(m => {
        const children = m.menu || (m.to ? [m] : []);
        if (children.length > 0) {
          newExpanded[m.id] = true;
        }
      });
      setExpandedModules(newExpanded);
    }
  };

  const handleGlobalSelectAll = () => {
    const newPerms = { ...permissions };
    modules.forEach(module => {
      const children = module.menu || (module.to ? [module] : []);
      children.forEach(child => {
        ['read', 'write', 'update', 'delete'].forEach(action => {
          const actionIdLower = child.id.toLowerCase();
          const isExemptModule = actionIdLower === 'active sessions' || actionIdLower === 'devices';
          if (isExemptModule && action !== 'read') return;
          const permissionType = action.charAt(0).toUpperCase() + action.slice(1);
          const permissionKey = `${module.id}-${child.id}-${child.id}${permissionType}`;
          newPerms[permissionKey] = true;
        });
      });
    });
    setPermissions(newPerms);
  };

  const handleGlobalClearAll = () => {
    setPermissions({});
  };

  const ACTION_LABELS = {
    read: 'View',
    write: 'Create',
    update: 'Edit',
    delete: 'Delete'
  };

  const handleCheckboxChange = (parentId, childId, action) => {
    const permissionType = action.charAt(0).toUpperCase() + action.slice(1);
    const permissionKey = `${parentId}-${childId}-${childId}${permissionType}`;
    setPermissions(prev => ({
      ...prev,
      [permissionKey]: !prev[permissionKey]
    }));
  };

  const getModuleSelectedCount = (parentId, children) => {
    if (!children || children.length === 0) return { selected: 0, total: 0 };
    let selected = 0;
    let total = 0;
    for (const child of children) {
      const actions = ['read', 'write', 'update', 'delete'];
      for (const action of actions) {
        const actionIdLower = child.id.toLowerCase();
        const isExemptModule = actionIdLower === 'active sessions' || actionIdLower === 'devices';
        if (isExemptModule && action !== 'read') continue;
        
        total++;
        const permissionType = action.charAt(0).toUpperCase() + action.slice(1);
        const permissionKey = `${parentId}-${child.id}-${child.id}${permissionType}`;
        if (permissions[permissionKey]) selected++;
      }
    }
    return { selected, total };
  };

  const handleModuleSelectAll = (parentId, children) => {
    const { selected, total } = getModuleSelectedCount(parentId, children);
    const allSelected = selected === total && total > 0;
    const newPerms = { ...permissions };
    
    for (const child of children) {
      const actions = ['read', 'write', 'update', 'delete'];
      for (const action of actions) {
        const actionIdLower = child.id.toLowerCase();
        const isExemptModule = actionIdLower === 'active sessions' || actionIdLower === 'devices';
        if (isExemptModule && action !== 'read') continue;
        
        const permissionType = action.charAt(0).toUpperCase() + action.slice(1);
        const permissionKey = `${parentId}-${child.id}-${child.id}${permissionType}`;
        newPerms[permissionKey] = !allSelected;
      }
    }
    setPermissions(newPerms);
  };

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await request('/role-master/list', { method: 'GET' });
      if (res?.success) setRoles(res.data || []);
      else {
        console.warn('fetchRoles response error:', res);
        toast('Error fetching roles', { variant: 'error' });
      }
    } catch (err) {
      console.error('fetchRoles error:', err);
      toast('Error fetching roles', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await request('/user', { method: 'GET' });
      if (Array.isArray(res)) setUsers(res);
      else if (res && res.data) setUsers(res.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, []);

  const renderUsers = (roleId) => {
    const roleUsers = users.filter(u => {
      const uRoleId = u.roleId || u.role?._id || u.role;
      return String(uRoleId) === String(roleId) || u.role?.role === roleId;
    });
    
    if (!roleUsers || roleUsers.length === 0) return <Typography component="span" sx={{ color: 'var(--slate)', fontFamily: 'Calibri', fontSize: '14px' }}>—</Typography>;
    
    const displayIds = roleUsers.slice(0, 3);
    const extraCount = roleUsers.length - 3;
    const colors = ['#E3F2FD', '#F3E5F5', '#E8F5E9', '#FFF3E0'];
    const textColors = ['#1976D2', '#7B1FA2', '#388E3C', '#F57C00'];
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {displayIds.map((item, index) => {
          const text = item.username || item.first_name || 'U';
          const initial = typeof text === 'string' && text.length > 0 ? text.charAt(0).toUpperCase() : '?';
          const cIdx = index % colors.length;
          return (
            <Tooltip key={item._id} title={text} arrow placement="top">
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: colors[cIdx],
                  color: textColors[cIdx],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Arial',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                {initial}
              </Box>
            </Tooltip>
          );
        })}
        {extraCount > 0 && (
          <Tooltip 
            title={roleUsers.map(x => x.username || x.first_name).join(', ')} 
            arrow 
            placement="top"
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: 'var(--mist)',
                color: 'var(--slate)',
                border: '1px solid var(--hairline)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Arial',
                fontSize: '13px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              +{extraCount}
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  };

  useEffect(() => {
    if (selectedRecord) {
      setValue('role', selectedRecord.role || '');
      setValue('description', selectedRecord.description || '');
      setValue('status', selectedRecord.status || 'Active');
      const perms = {};
      (selectedRecord.permissions || []).forEach(p => perms[p] = true);
      setPermissions(perms);
    } else {
      reset({ role: '', description: '', status: 'Active' });
      setPermissions({});
    }
    setExpandedModules({});
  }, [selectedRecord, setValue, reset]);

  const openDialog = (action, record) => {
    if (action === 'new' && !canCreate) return;
    if (action === 'edit' && !canEdit) return;
    clearErrors();
    setCurrentAction(action);
    setSelectedRecord(record);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    clearErrors();
    setCurrentAction(undefined);
    setSelectedRecord(undefined);
    reset({ role: '' });
  };

  const onSubmit = async (data) => {
    clearErrors();
    const isEdit = currentAction === 'edit';
    if (isEdit && !canEdit) return;
    if (!isEdit && !canCreate) return;

    const trimmedRoleName = data.role?.trim();
    if (!trimmedRoleName) {
      setError('role', { type: 'manual', message: 'Role name is required.' });
      return;
    }

    // Client-side duplicate check (case-insensitive)
    const isDuplicate = roles.some(r =>
      (!isEdit || r._id !== selectedRecord?._id) &&
      r.role?.trim().toLowerCase() === trimmedRoleName.toLowerCase()
    );

    if (isDuplicate) {
      setError('role', { type: 'manual', message: `Role "${trimmedRoleName}" already exists. Please choose a different name.` });
      return;
    }

    setLoading(true);
    try {
      const additionalPermissions = ["Exit", "Submit", "Cancel"];
      const selectedPermissions = Object.keys(permissions).filter(key => permissions[key] && !additionalPermissions.includes(key));
      let finalPermissions = [...new Set([...selectedPermissions, ...additionalPermissions])];

      const payload = {
        role: trimmedRoleName,
        description: data.description,
        status: data.status,
        permissions: finalPermissions
      };

      const endpoint = isEdit ? '/role-master/update' : '/role-master/create';
      const method = isEdit ? 'PUT' : 'POST';
      await request(endpoint, {
        method,
        params: isEdit ? selectedRecord._id : null,
        body: payload,
      });
      showSnack(`Role "${trimmedRoleName}" ${isEdit ? 'updated' : 'created'} successfully`, 'success');
      await fetchRoles();
      closeDialog();
    } catch (err) {
      console.error('onSubmit error:', err);
      const msg = err?.response?.data?.message || err?.message || 'Error saving role';
      if (msg.toLowerCase().includes('already exists')) {
        setError('role', { type: 'manual', message: msg });
      } else {
        showSnack(msg, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (record) => {
    if (!canDelete) return;
    setDeletingRole(record);
    setDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setDeletingRole(null);
    setDeleteOpen(false);
    setDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!deletingRole || !deletingRole._id) return;
    setDeleting(true);
    try {
      await request('/role-master/delete', { method: 'DELETE', params: deletingRole._id });
      toast('Role deleted', { variant: 'success' });
      showSnack(`Role "${deletingRole.role}" successfully deleted.`, 'success');
      await fetchRoles();
      handleCancelDelete();
    } catch (err) {
      console.error('onDelete error:', err);
      toast('Error deleting role', { variant: 'error' });
      showSnack('Error deleting role', 'error');
      setDeleting(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const isAllSelected = roles.length > 0 && selectedIds.length === roles.length;

  const toggleSelectAll = () => {
    if (isAllSelected) setSelectedIds([]);
    else setSelectedIds(roles.map(r => r._id));
  };

  const handleEditSelected = () => {
    if (!canEdit) return;
    if (selectedIds.length !== 1) return;
    const role = roles.find(r => r._id === selectedIds[0]);
    if (role) openDialog('edit', role);
    setSelectedIds([]);
  };

  const handleDeleteSelected = () => {
    if (!canDelete) return;
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleCancelBulkDelete = () => {
    setBulkDeleteOpen(false);
    setBulkDeleting(false);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all(selectedIds.map(id => request('/role-master/delete', { method: 'DELETE', params: id })));
      showSnack(`${selectedIds.length} role(s) deleted.`, 'success');
      setSelectedIds([]);
      await fetchRoles();
      handleCancelBulkDelete();
    } catch (err) {
      console.error('Bulk delete error', err);
      showSnack('Failed to delete selected roles.', 'error');
      setBulkDeleting(false);
    }
  };

  const showSnack = (message, severity = 'success') => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const getTotalCounts = () => {
    let selected = 0;
    let total = 0;
    modules.forEach(module => {
      const children = module.menu || (module.to ? [module] : []);
      const counts = getModuleSelectedCount(module.id, children);
      selected += counts.selected;
      total += counts.total;
    });
    return { selected, total };
  };
  const { selected: totalSelectedCount, total: totalPermsCount } = getTotalCounts();

  return (
    <Box p={2}>
      {/* Header and Breadcrumbs */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.95rem' }}>
          <Link to="/home" className="global-link" style={{ textDecoration: "none" }}>
            Dashboard
          </Link>
          <Typography className="global-section-title" sx={{ fontSize: '0.95rem', margin: 0 }}>
            Role Master
          </Typography>
        </Breadcrumbs>

        {canCreate && (
          <Button 
            size="small" 
            variant="contained" 
            onClick={() => openDialog('new')}
            className="global-button global-button-primary"
            sx={{ px: 2 }}
          >
            Add Role
          </Button>
        )}
      </Stack>

      {/* Bulk Action Bar */}
      {selectedIds.length > 0 && (
        <Box mb={2} display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" className="text-primary">
            {selectedIds.length} selected
          </Typography>
          
          {canEdit && (
            <Button 
              size="small" 
              className="global-button global-button-outlined" 
              onClick={handleEditSelected} 
              disabled={selectedIds.length !== 1}
            >
              Edit
            </Button>
          )}
          
          {canDelete && (
            <Button 
              size="small" 
              className="global-button global-button-danger" 
              onClick={handleDeleteSelected}
            >
              Delete
            </Button>
          )}
          
          <Button 
            size="small" 
            onClick={() => setSelectedIds([])} 
            className="global-button global-button-text"
          >
            Clear
          </Button>
        </Box>
      )}

      {/* Main Table Container */}
      <Paper 
        elevation={0} 
        sx={{
          bgcolor: 'var(--canvas)',
          border: '1px solid var(--hairline)',
          borderRadius: '12px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          mb: 3,
          boxShadow: 'none'
        }}
      >
        <Box
          sx={{
            overflowX: 'auto',
            width: '100%',
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--slate) transparent',
            '&::-webkit-scrollbar': { width: '6px', height: '6px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: 'var(--slate)', borderRadius: '10px' },
            flex: 1
          }}
        >
          <Box
            component="table"
            sx={{ 
              minWidth: 1000, 
              width: '100%', 
              borderCollapse: 'collapse',
              textAlign: 'left'
            }}
          >
            <Box component="thead" sx={{ bgcolor: '#FFF', borderBottom: '1px solid var(--hairline)' }}>
              <Box component="tr">
                <Box
                  component="th"
                  sx={{
                    p: '8px 16px',
                    width: 48,
                    textAlign: 'center',
                  }}
                >
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={selectedIds.length > 0 && !isAllSelected}
                    onChange={toggleSelectAll}
                    sx={{
                      p: '4px',
                      color: 'var(--slate)',
                      '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                        color: 'var(--edge-blue)',
                      },
                      '& .MuiSvgIcon-root': { fontSize: '20px', borderRadius: '4px' }
                    }}
                  />
                </Box>
                {[
                  { label: '#', width: '60px' },
                  { label: 'Role', width: 'auto' },
                  { label: 'Permissions', width: 'auto' },
                  { label: 'Users', width: 'auto' },
                  { label: 'Status', width: 'auto' },
                  ...(canEdit || canDelete ? [{ label: 'Actions', width: '80px' }] : [])
                ].map((col, i) => (
                  <Box
                    component="th"
                    key={i}
                    textAlign={col.label === 'Actions' ? 'center' : 'left'}
                    sx={{
                      p: '8px 16px',
                      width: col.width,
                      color: 'var(--vault-navy)',
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '13px',
                      fontWeight: 700,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {col.label}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {roles.length === 0 ? (
                <Box component="tr">
                  <Box component="td" colSpan={canEdit || canDelete ? 4 : 3} sx={{ p: '24px 16px', textAlign: 'center', color: 'var(--slate)', fontFamily: 'Calibri, Arial, sans-serif', fontSize: '16px' }}>
                    No roles found.
                  </Box>
                </Box>
              ) : (
                paginatedRoles.map((r, i) => (
                  <Box 
                    component="tr" 
                    key={r._id}
                    sx={{
                      bgcolor: '#FFF',
                      borderBottom: '1px solid var(--hairline)',
                      '&:hover': { bgcolor: 'var(--mist)' },
                    }}
                  >
                    <Box component="td" sx={{ p: '8px 16px', width: 48, textAlign: 'center', verticalAlign: 'middle' }}>
                      <Checkbox 
                        checked={selectedIds.includes(r._id)} 
                        onChange={() => toggleSelect(r._id)} 
                        sx={{
                          p: '4px',
                          color: 'var(--slate)',
                          '&.Mui-checked': {
                            color: 'var(--edge-blue)',
                          },
                          '& .MuiSvgIcon-root': { fontSize: '20px', borderRadius: '4px' }
                        }}
                      />
                    </Box>
                    <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle', fontFamily: 'Calibri, Arial, sans-serif', fontSize: '14px', color: 'var(--slate)' }}>
                      {page * rowsPerPage + i + 1}
                    </Box>
                    <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle', fontFamily: 'Arial, sans-serif', fontSize: '14px', fontWeight: 600, color: 'var(--vault-navy)' }}>
                      {r.role}
                    </Box>
                    <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle', fontFamily: 'Arial, sans-serif', fontSize: '14px', color: 'var(--slate)' }}>
                      {r.permissions ? [...new Set(r.permissions)].filter(p => !["Exit", "Submit", "Cancel"].includes(p)).length : 0} assigned
                    </Box>
                    <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle' }}>
                      {renderUsers(r._id)}
                    </Box>
                    <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle' }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, bgcolor: r.status === 'Inactive' ? 'var(--mist)' : 'rgba(0, 191, 99, 0.1)', color: r.status === 'Inactive' ? 'var(--slate)' : '#00bf63', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }} />
                        {r.status || 'Active'}
                      </Box>
                    </Box>
                    {(canEdit || canDelete) && (
                      <Box component="td" sx={{ p: '8px 16px', textAlign: 'center', verticalAlign: 'middle' }}>
                        <IconButton 
                          size="small" 
                          aria-controls={menuRowId === r._id ? `row-menu-${r._id}` : undefined} 
                          aria-haspopup="true" 
                          onClick={(e) => handleMenuOpen(e, r._id)} 
                          sx={{
                            width: '32px',
                            height: '32px',
                            color: 'var(--slate)',
                            borderRadius: '6px',
                            '&:hover': {
                              bgcolor: 'var(--mist)',
                              color: 'var(--vault-navy)',
                            }
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                        <Menu
                          id={`row-menu-${r._id}`}
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl) && menuRowId === r._id}
                          onClose={handleMenuClose}
                          anchorOrigin={{ vertical:'bottom', horizontal:'right' }}
                          transformOrigin={{ vertical:'top', horizontal:'right' }}
                          PaperProps={{ className: 'global-card' }}
                        >
                          <MenuItem onClick={() => onMenuView(r)}>View</MenuItem>
                          {canEdit && <MenuItem onClick={() => onMenuEdit(r)}>Edit</MenuItem>}
                          {canDelete && <MenuItem onClick={() => onMenuDelete(r)} className="global-text-critical">Delete</MenuItem>}
                        </Menu>
                      </Box>
                    )}
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>
        <TablePagination
          component="div"
          count={roles.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ flexShrink: 0 }}
        />
      </Paper>

      {/* Bulk Delete Dialog */}
      <CommonDeleteModal
        open={bulkDeleteOpen}
        onClose={handleCancelBulkDelete}
        onConfirm={handleConfirmBulkDelete}
        isDeleting={bulkDeleting}
        entityType="Role"
        isBulk={true}
        bulkCount={selectedIds.length}
      />

      {/* Add / Edit Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={closeDialog} 
        fullWidth 
        maxWidth="lg"
        sx={{
          '& .MuiDialog-paper': {
            width: 'min(1100px, calc(100vw - 32px)) !important',
            maxWidth: '1100px !important',
            borderRadius: '12px',
            overflow: 'hidden',
            m: 2
          },
        }}
        PaperProps={{
          className: 'global-card',
          sx: {
            p: 0,
            border: 'none',
            bgcolor: isDark ? 'var(--canvas)' : '#FFF'
          },
        }}
      >
        {currentAction === 'view' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '90vh', bgcolor: isDark ? 'var(--canvas)' : '#FFF' }}>
            {/* View Header */}
            <Box sx={{ p: 3, pb: 2, borderBottom: '1px solid var(--hairline)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--vault-navy)', fontSize: '20px' }}>
                  {selectedRecord?.role}
                </Typography>
                
                {selectedRecord?.description && (
                  <>
                    <Box sx={{ width: '4px', height: '4px', borderRadius: '50%', bgcolor: 'var(--slate)', opacity: 0.5 }} />
                    <Typography variant="body2" sx={{ color: 'var(--slate)', fontSize: '14px', maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {selectedRecord.description}
                    </Typography>
                  </>
                )}

                <Box sx={{ width: '4px', height: '4px', borderRadius: '50%', bgcolor: 'var(--slate)', opacity: 0.5 }} />

                {/* Status Pill */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.5, borderRadius: '20px', bgcolor: selectedRecord?.status === 'Active' ? 'rgba(0,191,99,0.1)' : 'rgba(0,0,0,0.05)', color: selectedRecord?.status === 'Active' ? '#00bf63' : 'var(--slate)', fontSize: '12px', fontWeight: 700 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }} />
                  {selectedRecord?.status || 'Active'}
                </Box>
                
                <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'rgba(0,0,0,0.05)', color: 'var(--vault-navy)', borderRadius: '16px', fontSize: '12px', fontWeight: 600 }}>
                  {users.filter(u => {
                    const uRoleId = u.roleId || u.role?._id || u.role;
                    return String(uRoleId) === String(selectedRecord?._id) || u.role?.role === selectedRecord?._id;
                  }).length} users
                </Box>
                
                <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'rgba(31, 111, 235, 0.1)', color: 'var(--edge-blue)', borderRadius: '16px', fontSize: '12px', fontWeight: 600 }}>
                  {totalSelectedCount} of {totalPermsCount} permissions
                </Box>
              </Box>

              <IconButton 
                onClick={closeDialog}
                sx={{ 
                  border: '1px solid var(--hairline)',
                  borderRadius: '8px',
                  p: '6px',
                  color: 'var(--vault-navy)',
                  bgcolor: 'transparent',
                  '&:hover': { bgcolor: 'var(--mist)' }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* View Body (Grid of modules) */}
            <Box sx={{ p: 4, flex: 1, overflowY: 'auto', bgcolor: isDark ? 'rgba(0,0,0,0.1)' : '#F8FAFC', '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { background: 'var(--hairline)', borderRadius: '10px' } }}>
              <Grid container spacing={3}>
                {modules.map(module => {
                  const children = module.menu || (module.to ? [module] : []);
                  if (children.length === 0) return null;
                  const { selected, total } = getModuleSelectedCount(module.id, children);
                  if (total === 0) return null;
                  
                  return (
                    <Grid item xs={12} sm={6} key={module.id}>
                      <Box sx={{ p: 3, bgcolor: isDark ? 'var(--canvas)' : '#FFF', border: '1px solid var(--hairline)', borderRadius: '12px', height: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography sx={{ fontWeight: 700, color: 'var(--vault-navy)', fontSize: '16px' }}>{module.Label}</Typography>
                          <Typography variant="body2" sx={{ color: 'var(--slate)', fontSize: '13px' }}>{selected} of {total}</Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {children.map(child => {
                            return ['read', 'write', 'update', 'delete'].map(action => {
                              const actionIdLower = child.id.toLowerCase();
                              const isExempt = actionIdLower === 'active sessions' || actionIdLower === 'devices';
                              if (isExempt && action !== 'read') return null;

                              const permType = action.charAt(0).toUpperCase() + action.slice(1);
                              const permKey = `${module.id}-${child.id}-${child.id}${permType}`;
                              const hasPerm = permissions[permKey];

                              return (
                                <Box key={`${child.id}-${action}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  {hasPerm ? (
                                    <CheckIcon sx={{ color: '#00bf63', fontSize: 18 }} />
                                  ) : (
                                    <CloseIcon sx={{ color: 'var(--slate)', fontSize: 18, opacity: 0.5 }} />
                                  )}
                                  <Typography sx={{ 
                                    fontSize: '14px', 
                                    color: hasPerm ? 'var(--vault-navy)' : 'var(--slate)', 
                                    textDecoration: hasPerm ? 'none' : 'line-through',
                                    opacity: hasPerm ? 1 : 0.6
                                  }}>
                                    {ACTION_LABELS[action]} {child.Label}
                                  </Typography>
                                </Box>
                              );
                            });
                          })}
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>

            {/* View Footer */}
            <Box sx={{ p: 3, borderTop: '1px solid var(--hairline)', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button onClick={closeDialog} sx={{ color: 'var(--vault-navy)', fontWeight: 700, textTransform: 'none', fontSize: '15px', border: '1px solid var(--hairline)', borderRadius: '8px', px: 3, '&:hover': { bgcolor: 'var(--mist)' } }}>
                Close
              </Button>
              {canEdit && (
                <Button onClick={() => setCurrentAction('edit')} variant="contained" sx={{ bgcolor: 'var(--edge-blue)', color: '#FFF', fontWeight: 700, textTransform: 'none', fontSize: '15px', borderRadius: '8px', px: 3, boxShadow: '0 4px 12px rgba(31, 111, 235, 0.2)', '&:hover': { bgcolor: '#1558C0' } }}>
                  Edit role
                </Button>
              )}
            </Box>
          </Box>
        ) : (
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh', bgcolor: isDark ? 'var(--canvas)' : '#FFF' }}>
              
              {/* Header */}
              <Box sx={{ p: 3, pb: 2, borderBottom: '1px solid var(--hairline)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--vault-navy)' }}>
                    {currentAction === 'edit' ? 'Edit role' : 'Create role'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--slate)' }}>
                    Name the role, then choose what it can do.
                  </Typography>
                </Box>
                <IconButton 
                  onClick={closeDialog}
                  sx={{ 
                    border: '1px solid var(--hairline)',
                    borderRadius: '8px',
                    p: '6px',
                    color: 'var(--vault-navy)',
                    bgcolor: 'transparent',
                    '&:hover': { bgcolor: 'var(--mist)' }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Two Column Layout */}
              <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', flexDirection: { xs: 'column', md: 'row' } }}>
                
                {/* Left Column: Role Details */}
                <Box sx={{ 
                  width: { xs: '100%', md: '40%' }, 
                  p: 3, 
                  borderRight: { md: '1px solid var(--hairline)' }, 
                  borderBottom: { xs: '1px solid var(--hairline)', md: 'none' }, 
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': { width: '6px' },
                  '&::-webkit-scrollbar-track': { background: 'transparent' },
                  '&::-webkit-scrollbar-thumb': { background: 'var(--hairline)', borderRadius: '10px' },
                  '&::-webkit-scrollbar-thumb:hover': { background: 'var(--slate)' }
                }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'var(--vault-navy)', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Role name <Typography component="span" sx={{ color: 'var(--critical)' }}>*</Typography>
                  </Typography>
                  <Controller
                    name="role"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Enter role name"
                        error={!!error}
                        helperText={error?.message}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <ShieldOutlinedIcon sx={{ color: 'var(--slate)', fontSize: 20 }} />
                            </InputAdornment>
                          ),
                          sx: { 
                            borderRadius: '8px',
                            '& fieldset': { borderColor: 'var(--hairline)' },
                            '&:hover fieldset': { borderColor: 'var(--slate)' },
                            '&.Mui-focused fieldset': { borderColor: 'var(--edge-blue)' },
                            bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : '#FFF'
                          }
                        }}
                      />
                    )}
                  />
                  <Typography variant="body2" sx={{ color: 'var(--slate)', mt: 0.5, fontSize: '13px' }}>
                    Shown in the role list and when assigning users.
                  </Typography>

                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'var(--vault-navy)', mt: 3, mb: 1 }}>
                    Description
                  </Typography>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="What this role is for."
                        InputProps={{
                          sx: { 
                            borderRadius: '8px',
                            '& fieldset': { borderColor: 'var(--hairline)' },
                            '&:hover fieldset': { borderColor: 'var(--slate)' },
                            '&.Mui-focused fieldset': { borderColor: 'var(--edge-blue)' },
                            bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : '#FFF'
                          }
                        }}
                      />
                    )}
                  />
                  <Typography variant="body2" sx={{ color: 'var(--slate)', mt: 0.5, fontSize: '13px' }}>
                    What this role is for.
                  </Typography>

                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'var(--vault-navy)', mt: 3, mb: 1 }}>
                    Status
                  </Typography>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {['Active', 'Inactive'].map(opt => (
                          <Button
                            key={opt}
                            variant={field.value === opt ? 'contained' : 'outlined'}
                            onClick={() => field.onChange(opt)}
                            sx={{
                              borderRadius: '20px',
                              textTransform: 'none',
                              px: 3,
                              bgcolor: field.value === opt ? (opt === 'Active' ? '#00bf63' : 'var(--slate)') : 'transparent',
                              borderColor: field.value === opt ? 'transparent' : 'var(--hairline)',
                              color: field.value === opt ? '#FFF' : 'var(--slate)',
                              '&:hover': {
                                bgcolor: field.value === opt ? (opt === 'Active' ? '#00994f' : 'var(--slate)') : 'rgba(0,0,0,0.05)',
                              }
                            }}
                          >
                            {opt}
                          </Button>
                        ))}
                      </Box>
                    )}
                  />
                  <Typography variant="body2" sx={{ color: 'var(--slate)', mt: 0.5, fontSize: '13px' }}>
                    Inactive roles can't be assigned to new users.
                  </Typography>
                </Box>

                {/* Right Column: Permissions */}
                <Box sx={{ width: { xs: '100%', md: '60%' }, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ p: 3, pb: 2, borderBottom: '1px solid var(--hairline)', bgcolor: isDark ? 'rgba(0,0,0,0.2)' : '#F8FAFC' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography sx={{ fontSize: '16px', fontWeight: 700, color: 'var(--vault-navy)' }}>
                        Permissions
                      </Typography>
                      <Box sx={{ px: 2, py: 0.5, bgcolor: 'rgba(31, 111, 235, 0.1)', color: 'var(--edge-blue)', borderRadius: '16px', fontSize: '13px', fontWeight: 600 }}>
                        {totalSelectedCount} of {totalPermsCount} selected
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'var(--slate)', fontSize: '13px' }}>
                      Each feature has its own actions. Create, edit and similar actions need the feature's View permission, so it's selected for you.
                    </Typography>
                  </Box>

                  <Box sx={{ 
                    flex: 1, 
                    p: 3, 
                    overflowY: 'auto', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 1.5, 
                    bgcolor: isDark ? 'var(--canvas)' : '#FFF',
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { background: 'var(--hairline)', borderRadius: '10px' },
                    '&::-webkit-scrollbar-thumb:hover': { background: 'var(--slate)' }
                  }}>
                    {modules.map(module => {
                      const children = module.menu || (module.to ? [module] : []);
                      if (children.length === 0) return null;
                      const { selected, total } = getModuleSelectedCount(module.id, children);
                      const allSelected = selected === total && total > 0;

                      return (
                        <Accordion 
                          key={module.id} 
                          expanded={!!expandedModules[module.id]}
                          onChange={(e, isExpanded) => setExpandedModules(prev => ({ ...prev, [module.id]: isExpanded }))}
                          sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#F8FAFC', border: '1px solid var(--hairline)', boxShadow: 'none', '&:before': { display: 'none' }, borderRadius: '8px !important' }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                              <Typography sx={{ fontWeight: 600, color: 'var(--vault-navy)' }}>{module.Label}</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body2" sx={{ color: 'var(--slate)' }}>
                                  {selected} of {total}
                                </Typography>
                                <Button
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); handleModuleSelectAll(module.id, children); }}
                                  sx={{ color: 'var(--edge-blue)', textTransform: 'none', fontWeight: 600, p: 0, minWidth: 'auto' }}
                                >
                                  {allSelected ? 'Deselect all' : 'Select all'}
                                </Button>
                              </Box>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ borderTop: '1px solid var(--hairline)', pt: 2, pb: 3, px: 3 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                              {children.map(child => (
                                <Box key={child.id} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  {children.length > 1 && (
                                    <Typography sx={{ fontWeight: 600, color: 'var(--vault-navy)', fontSize: '14px', borderBottom: '1px solid var(--hairline)', pb: 0.5, mb: 0.5 }}>{child.Label}</Typography>
                                  )}
                                  <Grid container spacing={1.5}>
                                    {['read', 'write', 'update', 'delete'].map(action => {
                                      const actionIdLower = child.id.toLowerCase();
                                      const isExemptModule = actionIdLower === 'active sessions' || actionIdLower === 'devices';
                                      if (isExemptModule && action !== 'read') return null;

                                      const permissionType = action.charAt(0).toUpperCase() + action.slice(1);
                                      const permissionKey = `${module.id}-${child.id}-${child.id}${permissionType}`;
                                      const isChecked = permissions[permissionKey] || false;

                                      return (
                                        <Grid item xs={12} sm={6} key={action}>
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                size="small"
                                                checked={isChecked}
                                                onChange={() => handleCheckboxChange(module.id, child.id, action)}
                                                sx={{ p: 0.5, color: 'var(--slate)', '&.Mui-checked': { color: 'var(--edge-blue)' } }}
                                              />
                                            }
                                            label={<Typography variant="body2" sx={{ color: 'var(--vault-navy)', fontSize: '14px', whiteSpace: 'nowrap' }}>{ACTION_LABELS[action]} {child.Label}</Typography>}
                                            sx={{ ml: 0, '& .MuiFormControlLabel-label': { ml: 1 } }}
                                          />
                                        </Grid>
                                      );
                                    })}
                                  </Grid>
                                </Box>
                              ))}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                </Box>
              </Box>

              {/* Footer */}
              <Box sx={{ 
                p: 3, 
                borderTop: '1px solid var(--hairline)', 
                display: 'flex', 
                justifyContent: 'flex-end',
                gap: 2
              }}>
                <Button 
                  onClick={closeDialog}
                  disabled={loading}
                  sx={{ 
                    color: 'var(--vault-navy)', 
                    fontWeight: 700, 
                    textTransform: 'none',
                    fontSize: '15px',
                    bgcolor: 'transparent',
                    border: '1px solid var(--hairline)',
                    borderRadius: '8px',
                    px: 3,
                    '&:hover': { bgcolor: 'var(--mist)' }
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  disabled={loading}
                  variant="contained"
                  sx={{
                    bgcolor: 'var(--edge-blue)',
                    color: '#FFF',
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: '15px',
                    borderRadius: '8px',
                    px: 3,
                    boxShadow: '0 4px 12px rgba(31, 111, 235, 0.2)',
                    '&:hover': { bgcolor: '#1558C0' }
                  }}
                >
                  {loading ? (currentAction === 'edit' ? 'Updating…' : 'Saving…') : (currentAction === 'edit' ? 'Update role' : 'Create role')}
                </Button>
              </Box>
            </Box>
            </form>
          </FormProvider>
        )}
      </Dialog>

      {/* Single Delete Dialog */}
      <CommonDeleteModal
        open={deleteOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isDeleting={deleting}
        entityName={deletingRole ? deletingRole.role : ""}
        entityType="Role"
      />

      {/* Snackbar Alerts */}
      <Snackbar 
        open={snackOpen} 
        autoHideDuration={4000} 
        onClose={(e, r) => { if (r !== 'clickaway') setSnackOpen(false) }} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert 
          onClose={(e, r) => { if (r !== 'clickaway') setSnackOpen(false) }} 
          severity={snackSeverity} 
          sx={{ width: '100%', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
          className="global-alert"
        >
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};